import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { KfOnboardingModal } from "../components/knightsfee/KfOnboardingModal";
import { KfPostPolicyModal, hasSeenKfPostPolicy } from "../components/knightsfee/KfPostPolicyModal";
import { KfComposeModal } from "../components/knightsfee/KfComposeModal";
import { KfPostCard } from "../components/knightsfee/KfPostCard";
import { KfProfileCard } from "../components/knightsfee/KfProfileCard";
import { DragonCompanion } from "../components/knightsfee/DragonCompanion";
import { ArrowLeft, PenSquare, ImagePlus, Scroll } from "lucide-react";

const KF_BG_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_adventure-hub-220/artifacts/6ty9i36p_kights%20fee.png";

export default function KnightsFeePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [feedMode, setFeedMode] = useState("discover");
  const [lfgKindFilter, setLfgKindFilter] = useState("all");
  const [lfgSystemFilter, setLfgSystemFilter] = useState("all");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [profile, setProfile] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeAutoPicker, setComposeAutoPicker] = useState(false);
  const [postPolicyOpen, setPostPolicyOpen] = useState(false);
  const [pendingAutoPicker, setPendingAutoPicker] = useState(false);
  const sentinelRef = useRef(null);

  const lfgParams = () => (feedMode === "lfg" ? { lfg_kind: lfgKindFilter !== "all" ? lfgKindFilter : undefined, lfg_system: lfgSystemFilter !== "all" ? lfgSystemFilter : undefined } : {});

  const loadFeed = async (mode) => {
    setLoading(true);
    try {
      const { data } = await api.get("/kf/feed", { params: { mode, ...lfgParams() } });
      setPosts(data);
      setHasMore(data.length === 20);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (posts.length === 0) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/kf/feed", { params: { mode: feedMode, before: posts[posts.length - 1].created_at, ...lfgParams() } });
      setPosts((prev) => [...prev, ...data]);
      setHasMore(data.length === 20);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadFeed(feedMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedMode, lfgKindFilter, lfgSystemFilter]);

  useEffect(() => {
    if (user?.id) api.get(`/kf/profile/${user.id}`).then((r) => setProfile(r.data));
  }, [user?.id]);

  // Infinite scroll: a 1px sentinel sits just past the last loaded post. The moment it enters
  // the viewport (200px early, via rootMargin, so the next page is ready before you hit bottom)
  // we fetch the next page — replaces a manual "Load More" click entirely. Re-created whenever
  // the feed itself changes so it always closes over the current page/cursor.
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "200px" });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore, posts]);

  const openCompose = (autoPicker) => {
    if (!hasSeenKfPostPolicy(user?.id)) {
      setPendingAutoPicker(autoPicker);
      setPostPolicyOpen(true);
      return;
    }
    setComposeAutoPicker(autoPicker);
    setComposeOpen(true);
  };

  const handlePosted = (post) => {
    const belongsInCurrentTab = feedMode === "lfg" ? post.post_type === "lfg" : post.post_type !== "lfg";
    if (belongsInCurrentTab) setPosts((prev) => [post, ...prev]);
  };
  const handleDeleted = (postId) => setPosts((prev) => prev.filter((p) => p.id !== postId));

  return (
    <div className="relative min-h-screen text-[#f1e0c5] bg-[#151613] bg-cover bg-center bg-fixed" style={{ backgroundImage: `url('${KF_BG_URL}')` }} data-testid="knights-fee-page">
      <div className="fixed inset-0 bg-black/70 pointer-events-none" data-testid="kf-bg-overlay" />
      <KfOnboardingModal userId={user?.id} />
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-white/5">
        <div className="flex items-center gap-4">
          <button data-testid="kf-back-to-dashboard-button" onClick={() => navigate("/dashboard")} className="text-[#f1e0c5]/50 hover:text-[#D4AF37]">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-display text-2xl flex items-center gap-2">
            <Scroll className="w-6 h-6 text-[#D4AF37]" /> Knights Fee
          </span>
        </div>
        <span className="flex items-center gap-3">
          <DragonCompanion />
          <span className="text-sm text-[#f1e0c5]/60">{user?.name}</span>
        </span>
      </nav>

      <div className="relative z-10 px-6 md:px-12 py-8 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-4 order-2 md:order-1">
          <Tabs value={feedMode} onValueChange={setFeedMode}>
            <TabsList className="bg-black/30" data-testid="kf-feed-tabs">
              <TabsTrigger data-testid="kf-tab-following" value="following">Following</TabsTrigger>
              <TabsTrigger data-testid="kf-tab-discover" value="discover">Discover</TabsTrigger>
              <TabsTrigger data-testid="kf-tab-lfg" value="lfg">Call to Arms</TabsTrigger>
            </TabsList>
          </Tabs>

          {feedMode === "lfg" && (
            <div className="flex flex-wrap gap-2" data-testid="kf-lfg-filters">
              <Select value={lfgKindFilter} onValueChange={setLfgKindFilter}>
                <SelectTrigger data-testid="kf-lfg-filter-kind" className="bg-black/30 border-white/10 w-44 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Kind</SelectItem>
                  <SelectItem value="looking_for_players">Looking for Players</SelectItem>
                  <SelectItem value="looking_for_table">Looking for a Table</SelectItem>
                </SelectContent>
              </Select>
              <Select value={lfgSystemFilter} onValueChange={setLfgSystemFilter}>
                <SelectTrigger data-testid="kf-lfg-filter-system" className="bg-black/30 border-white/10 w-40 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any System</SelectItem>
                  <SelectItem value="pf2e">Pathfinder 2e</SelectItem>
                  <SelectItem value="dnd5e">D&D 5e</SelectItem>
                  <SelectItem value="homebrew">Homebrew</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {loading ? (
            <p className="text-sm text-[#f1e0c5]/40 py-10 text-center">Loading the feed…</p>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 text-[#f1e0c5]/40 text-sm" data-testid="kf-feed-empty">
              {feedMode === "following"
                ? "Follow some fellow Knights to see their posts here — or check Discover to find them."
                : feedMode === "lfg"
                ? "No open calls right now — be the first to post a Looking for Group listing!"
                : "No posts yet — be the first Knight to share something!"}
            </div>
          ) : (
            <div className="space-y-4" data-testid="kf-feed-list">
              {posts.map((post) => (
                <KfPostCard key={post.id} post={post} myUserId={user?.id} onDeleted={handleDeleted} />
              ))}
              {hasMore && (
                <div ref={sentinelRef} data-testid="kf-feed-sentinel" className="h-4 flex items-center justify-center">
                  {loadingMore && <span className="text-[11px] uppercase tracking-wider text-[#f1e0c5]/30">Loading more…</span>}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4 order-1 md:order-2">
          {profile && <KfProfileCard profile={profile} onProfileUpdate={setProfile} onFollowChange={setProfile} />}
          <div className="rounded-xl border border-white/10 bg-black/20 p-3 flex md:flex-col gap-2">
            <Button data-testid="kf-rail-post-button" onClick={() => openCompose(false)} className="flex-1 rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-wider text-xs gap-2 justify-center">
              <PenSquare className="w-4 h-4" /> Post
            </Button>
            <Button data-testid="kf-rail-add-picture-button" onClick={() => openCompose(true)} variant="secondary" className="flex-1 rounded-full uppercase tracking-wider text-xs gap-2 justify-center">
              <ImagePlus className="w-4 h-4" /> Add Picture
            </Button>
          </div>
        </div>
      </div>

      <KfComposeModal open={composeOpen} onOpenChange={setComposeOpen} autoOpenPicker={composeAutoPicker} onPosted={handlePosted} />
      <KfPostPolicyModal
        open={postPolicyOpen}
        onOpenChange={setPostPolicyOpen}
        userId={user?.id}
        onContinue={() => { setComposeAutoPicker(pendingAutoPicker); setComposeOpen(true); }}
      />
    </div>
  );
}
